import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import ProductCard from "../components/ProductCard";
import QuoteDialog from "../components/QuoteDialog";
import { PRODUCTS, PRODUCT_CATEGORIES } from "../lib/products";

import SEO from "../components/SEO";
export default function Products() {
  const [searchParams, setSearchParams] = useSearchParams();
  const category = searchParams.get("category") || "All";
  const series = searchParams.get("series") || "All";
  const [quoteOpen, setQuoteOpen] = useState(false);
  const [quoteProduct, setQuoteProduct] = useState(null);

  const categoryProducts = useMemo(() => {
    if (category === "All") return PRODUCTS;
    return PRODUCTS.filter((p) => p.productCategory === category);
  }, [category]);

  const availableSeries = useMemo(
    () => [...new Set(categoryProducts.map((p) => p.series).filter(Boolean))],
    [categoryProducts]
  );

  const filtered = useMemo(() => {
    if (series === "All") return categoryProducts;
    return categoryProducts.filter((p) => p.series === series);
  }, [categoryProducts, series]);

  const setCategory = (nextCategory) => {
    const params = new URLSearchParams(searchParams);
    if (nextCategory === "All") params.delete("category");
    else params.set("category", nextCategory);
    params.delete("series");
    setSearchParams(params);
  };

  const setSeries = (nextSeries) => {
    const params = new URLSearchParams(searchParams);
    if (nextSeries === "All") params.delete("series");
    else params.set("series", nextSeries);
    setSearchParams(params);
  };

  const pageTitle = category === "All" ? "Products" : category;
  const pageDescription =
    category === "All"
      ? "Browse hand gloves and safety gloves for bulk industrial and commercial requirements, including rubber, cotton and industrial coated hand gloves."
      : PRODUCT_CATEGORIES.find((item) => item.name === category)?.description ||
        "Reliable hand protection for industrial and general use.";

  const seoTitleByCategory = {
    "Rubber Hand Gloves": "Rubber Hand Gloves Manufacturer in India",
    "Cotton Hand Gloves": "Cotton Hand Gloves Supplier in India",
    "Industrial Coated Hand Gloves": "Industrial Safety Gloves & Coated Hand Gloves",
  };

  const seoDescriptionByCategory = {
    "Rubber Hand Gloves":
      "Explore RSN rubber hand gloves manufactured in India for industrial, commercial and general-purpose use. Bulk supply for dealers, distributors and industrial buyers.",
    "Cotton Hand Gloves":
      "Explore cotton hand gloves including knitted, dotted, jeans, canvas and leather-reinforced gloves for industrial handling and bulk supply across India.",
    "Industrial Coated Hand Gloves":
      "Explore industrial safety gloves including nitrile coated and cut-resistant hand gloves for demanding handling applications and bulk supply across India.",
  };

  const seoTitle =
    category === "All"
      ? "Hand Gloves & Safety Gloves Supplier in India"
      : seoTitleByCategory[category] || `${category} | Hand Gloves`;

  const seoDescription =
    category === "All"
      ? "Browse RSN hand gloves and safety gloves: rubber hand gloves, cotton hand gloves and industrial coated gloves for dealers, distributors and industrial buyers across India."
      : seoDescriptionByCategory[category] || pageDescription;

  return (
    <>
      <SEO
        title={seoTitle}
        description={seoDescription}
        path="/products"
      />
    <div data-testid="products-page" className="bg-[#FBFCFA] min-h-screen">
      <section className="bg-white border-b border-[#E4EBE6]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10 sm:py-12">
          <div className="w-10 h-[2px] bg-[#075B3A] mb-4" />
          <h1 className="font-display text-4xl sm:text-5xl font-black text-[#0F172A]">{pageTitle}</h1>
          <p className="mt-3 text-[#5F6B66] max-w-2xl">{pageDescription}</p>
        </div>
      </section>

      {/* MOBILE CATEGORY NAVIGATION */}
      <div className="lg:hidden bg-white border-b border-[#E4EBE6] overflow-x-auto">
        <div className="px-4 py-4 flex gap-2 min-w-max">
          <FilterPill active={category === "All"} onClick={() => setCategory("All")}>All Products</FilterPill>
          {PRODUCT_CATEGORIES.map((item) => (
            <FilterPill key={item.slug} active={category === item.name} onClick={() => setCategory(item.name)}>
              {item.name}
            </FilterPill>
          ))}
        </div>
      </div>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
        <div className="grid lg:grid-cols-[230px_1fr] gap-6 lg:gap-8 items-start">
          {/* SIDEBAR - CATEGORY NAVIGATION UNCHANGED IN FUNCTION */}
          <aside className="hidden lg:block sticky top-24 soft-card overflow-hidden">
            <div className="px-5 py-5 border-b border-[#E5ECE7]">
              <div className="font-display text-xl font-black text-[#0F172A]">Browse Products</div>
              <div className="w-9 h-[2px] bg-[#075B3A] mt-3" />
            </div>

            <div className="p-3">
              <SidebarButton active={category === "All"} onClick={() => setCategory("All")}>
                All Products
              </SidebarButton>
              {PRODUCT_CATEGORIES.map((item) => (
                <SidebarButton key={item.slug} active={category === item.name} onClick={() => setCategory(item.name)}>
                  {item.name}
                </SidebarButton>
              ))}
            </div>

            {availableSeries.length > 1 && (
              <div className="border-t border-[#E5ECE7] px-5 py-5">
                <div className="font-display text-lg font-bold text-[#0F172A]">Filter by Range</div>
                <div className="mt-4 space-y-2">
                  <button
                    type="button"
                    onClick={() => setSeries("All")}
                    className={`w-full text-left text-sm py-2 transition-colors ${
                      series === "All" ? "font-bold text-[#075B3A]" : "text-[#5F6B66] hover:text-[#075B3A]"
                    }`}
                  >
                    All
                  </button>
                  {availableSeries.map((item) => (
                    <button
                      key={item}
                      type="button"
                      onClick={() => setSeries(item)}
                      className={`w-full text-left text-sm py-2 transition-colors ${
                        series === item ? "font-bold text-[#075B3A]" : "text-[#5F6B66] hover:text-[#075B3A]"
                      }`}
                    >
                      {item}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </aside>

          <div className="soft-card p-4 sm:p-6 lg:p-7 min-w-0">
            <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-6">
              <div>
                <h2 className="font-display text-2xl sm:text-3xl font-black text-[#0F172A]">{pageTitle}</h2>
                <p className="text-sm text-[#66736C] mt-1">{filtered.length} product{filtered.length === 1 ? "" : "s"}</p>
              </div>

              {availableSeries.length > 1 && (
                <div className="lg:hidden flex gap-2 overflow-x-auto pb-1">
                  <FilterPill active={series === "All"} onClick={() => setSeries("All")}>All</FilterPill>
                  {availableSeries.map((item) => (
                    <FilterPill key={item} active={series === item} onClick={() => setSeries(item)}>{item}</FilterPill>
                  ))}
                </div>
              )}
            </div>

            <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-5">
              {filtered.map((product) => (
                <ProductCard
                  key={product.slug}
                  product={product}
                  onQuote={(selected) => {
                    setQuoteProduct(selected);
                    setQuoteOpen(true);
                  }}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      <QuoteDialog open={quoteOpen} onOpenChange={setQuoteOpen} product={quoteProduct} />
    </div>
    </>
  );
}

function SidebarButton({ active, onClick, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`w-full text-left px-3 py-3 rounded-md text-sm font-semibold transition-colors ${
        active ? "bg-[#E8F3EE] text-[#075B3A]" : "text-[#0F172A] hover:bg-[#F5F8F6]"
      }`}
    >
      {children}
    </button>
  );
}

function FilterPill({ active, onClick, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-4 py-2 rounded-full border text-xs font-bold whitespace-nowrap transition-colors ${
        active
          ? "bg-[#075B3A] border-[#075B3A] text-white"
          : "bg-white border-[#D9E4DD] text-[#425049] hover:border-[#075B3A] hover:text-[#075B3A]"
      }`}
    >
      {children}
    </button>
  );
}
